import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, TrendingUp, Search, MapPin, Shield, Activity, Calendar, RefreshCw, BarChart3, Download, Database, Upload, AlertTriangle, XCircle, Map } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, AreaChart, Area, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Treemap,
} from 'recharts';

const COLORS = ['#0d9488', '#f97316', '#3b82f6', '#8b5cf6', '#ef4444', '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6'];

const CustomTooltipArea = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-primary">{payload[0].value} buscas</p>
    </div>
  );
};

const CustomTooltipBar = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
      <p className="text-sm font-medium text-slate-800">{payload[0].payload.name}</p>
      <p className="text-sm font-semibold text-primary">{payload[0].payload.pct}%</p>
    </div>
  );
};

const treemapDataGlobal = { current: [] };

const TreemapContent = (props) => {
  const { x, y, width, height, index, fill } = props;
  const item = treemapDataGlobal.current[index];
  if (!item || width < 50 || height < 30) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill || item.fill} stroke="#fff" strokeWidth={2} rx={4} />
      <text x={x + width / 2} y={y + height / 2} textAnchor="middle" fill="#fff" fontSize={width < 80 ? 10 : 12} fontWeight={600}>
        {item.name?.length > 15 ? item.name.slice(0, 14) + '…' : item.name}
      </text>
    </g>
  );
};

// Brazil State Grid Map — approximate geographic positions
const BRAZIL_GRID = [
  [null, null, 'RR', 'AP', null, null, null, null, null],
  ['AM', 'AM', 'PA', 'PA', 'MA', 'CE', 'RN', 'PB', null],
  ['AC', null, 'TO', 'PI', null, 'PE', 'AL', 'SE', null],
  ['RO', null, 'MT', 'GO', 'DF', 'BA', 'BA', null, null],
  [null, null, 'MS', null, 'MG', 'MG', 'ES', null, null],
  [null, null, null, 'SP', 'SP', 'RJ', null, null, null],
  [null, null, null, 'PR', null, null, null, null, null],
  [null, null, null, 'SC', null, null, null, null, null],
  [null, null, 'RS', null, null, null, null, null, null],
];

const STATE_NAMES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapa', AM: 'Amazonas', BA: 'Bahia',
  CE: 'Ceara', DF: 'Distrito Federal', ES: 'Espirito Santo', GO: 'Goias',
  MA: 'Maranhao', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Para', PB: 'Paraiba', PR: 'Parana', PE: 'Pernambuco', PI: 'Piaui',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
  RO: 'Rondonia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'Sao Paulo',
  SE: 'Sergipe', TO: 'Tocantins',
};

const getHeatColor = (ratio, maxRatio) => {
  if (ratio === 0) return '#f1f5f9';
  const intensity = Math.min(ratio / Math.max(maxRatio, 1), 1);
  if (intensity > 0.7) return '#dc2626';
  if (intensity > 0.4) return '#f97316';
  if (intensity > 0.15) return '#eab308';
  return '#22c55e';
};

const BrazilStateHeatmap = ({ data }) => {
  const stateMap = {};
  data.forEach(s => { stateMap[s.state] = s; });
  const maxRatio = Math.max(...data.map(s => s.ratio), 1);
  const rendered = new Set();

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-8" data-testid="state-heatmap">
      <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Map className="w-5 h-5 text-primary" /> Mapa de Calor por Estado
      </h3>
      <p className="text-xs text-slate-400 mb-4 ml-7">Intensidade = buscas / profissionais cadastrados. Vermelho = alta demanda sem oferta.</p>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 flex justify-center">
          <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: 'repeat(9, 44px)', gridTemplateRows: 'repeat(9, 38px)' }}>
            {BRAZIL_GRID.flat().map((uf, i) => {
              if (!uf) return <div key={`empty-${i}`} />;
              if (rendered.has(`${Math.floor(i/9)}-${uf}`)) return <div key={`dup-${i}`} />;
              rendered.add(`${Math.floor(i/9)}-${uf}`);
              const info = stateMap[uf] || { searches: 0, doctors: 0, ratio: 0 };
              const color = getHeatColor(info.ratio, maxRatio);
              const isActive = info.searches > 0 || info.doctors > 0;
              return (
                <div
                  key={`state-${uf}`}
                  className={`rounded-md flex flex-col items-center justify-center text-center transition-all cursor-default ${isActive ? 'shadow-sm hover:scale-110 hover:z-10' : 'opacity-40'}`}
                  style={{ backgroundColor: color }}
                  title={`${STATE_NAMES[uf] || uf}: ${info.searches} buscas, ${info.doctors} médicos`}
                >
                  <span className="text-[10px] font-bold text-white drop-shadow-sm">{uf}</span>
                  {isActive && <span className="text-[8px] text-white/80">{info.searches}/{info.doctors}</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="lg:w-56 space-y-2">
          <p className="text-xs font-medium text-slate-600 mb-2">Legenda (buscas/médicos)</p>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-3 rounded-sm" style={{ backgroundColor: '#dc2626' }} />
            Critico (alta demanda, sem oferta)
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-3 rounded-sm" style={{ backgroundColor: '#f97316' }} />
            Alto (muita demanda, pouca oferta)
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-3 rounded-sm" style={{ backgroundColor: '#eab308' }} />
            Medio (demanda moderada)
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-3 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
            Bom (oferta atende demanda)
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-3 rounded-sm" style={{ backgroundColor: '#f1f5f9' }} />
            Sem dados
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
            <p className="text-xs font-medium text-slate-700">Top Estados Criticos:</p>
            {data.filter(s => s.searches > 0).slice(0, 4).map((s) => (
              <p key={s.state} className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{s.state}</span>: {s.searches} buscas / {s.doctors} med.
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LostOpportunitiesTable = ({ data }) => (
  <div className="bg-white rounded-2xl border border-red-100 p-5 mb-8" data-testid="lost-opportunities">
    <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
      <XCircle className="w-5 h-5 text-red-500" /> Oportunidades Perdidas
    </h3>
    <p className="text-xs text-slate-400 mb-4 ml-7">Buscas com 0 resultados — demanda real sem nenhuma oferta na plataforma.</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-red-50/50 border-b border-red-100">
          <tr>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">#</th>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">Especialidade</th>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">Cidade</th>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">Estado</th>
            <th className="px-3 py-2.5 text-right font-medium text-slate-500">Buscas sem resultado</th>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">?ltima busca</th>
            <th className="px-3 py-2.5 text-left font-medium text-slate-500">Prioridade</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => {
            const priority = item.count >= 5 ? 'Critica' : item.count >= 3 ? 'Alta' : item.count >= 2 ? 'Media' : 'Baixa';
            const priorityColor = item.count >= 5 ? 'bg-red-500' : item.count >= 3 ? 'bg-orange-500' : item.count >= 2 ? 'bg-amber-500' : 'bg-slate-400';
            return (
              <tr key={`${item.specialty}-${item.city}-${idx}`} className="border-b border-slate-50 hover:bg-red-50/30">
                <td className="px-3 py-2.5">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white bg-red-500">{idx + 1}</span>
                </td>
                <td className="px-3 py-2.5 font-medium text-slate-700">{item.specialty}</td>
                <td className="px-3 py-2.5 text-slate-600">{item.city}</td>
                <td className="px-3 py-2.5 text-slate-600">{item.state}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-red-600">{item.count}</td>
                <td className="px-3 py-2.5 text-slate-500 text-xs">
                  {new Date(item.last_search).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center justify-center min-w-[60px] h-5 rounded-md text-[10px] font-bold text-white px-1.5 ${priorityColor}`}>
                    {priority}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default function AdminSearchAnalytics() {
  const { api, triggerDownload } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [restoring, setRestoring] = useState(false);
  const [restoringCnpjs, setRestoringCnpjs] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(null);
  const [restoreLogsProgress, setRestoreLogsProgress] = useState(null);
  const [activeTab, setActiveTab] = useState('searches');
  const [cnpjStats, setCnpjStats] = useState(null);
  const [loadingCnpjStats, setLoadingCnpjStats] = useState(false);
  const fileInputRef = React.useRef(null);
  const cnpjFileInputRef = React.useRef(null);

  useEffect(() => { fetchData(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'cnpjs' && !cnpjStats && !loadingCnpjStats) {
      fetchCnpjStats();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCnpjStats = async () => {
    setLoadingCnpjStats(true);
    try {
      const res = await api.get('/admin/stats');
      setCnpjStats(res.data);
    } catch (e) {
      // silent — UI will show empty state
    } finally {
      setLoadingCnpjStats(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/search-analytics?days=${days}`);
      setData(res.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      alert('Selecione um arquivo .xlsx');
      return;
    }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const start = await api.post('/admin/restore-search-logs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const taskId = start.data.task_id;
      if (!taskId) {
        // Fallback for older endpoint shape.
        alert(start.data.message || 'Conclu?do');
        fetchData();
        return;
      }
      let attempts = 0;
      const MAX_ATTEMPTS = 200;
      while (attempts < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 3000));
        attempts += 1;
        let st;
        try {
          st = await api.get(`/admin/restore-search-logs/status/${taskId}`);
        } catch {
          continue;
        }
        const s = st.data || {};
        setRestoreLogsProgress(s);
        if (s.status === 'completed') {
          alert(s.message || `${s.imported || 0} importados, ${s.skipped || 0} ignorados.`);
          fetchData();
          return;
        }
        if (s.status === 'failed') {
          alert(`Erro ao restaurar: ${s.error || 'desconhecido'}`);
          return;
        }
      }
      alert('Restauracao demorou mais que o esperado. Verifique o status mais tarde.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao restaurar: ' + err.message);
    } finally {
      setRestoring(false);
      setRestoreLogsProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRestoreCnpjs = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      alert('Selecione um arquivo .xlsx');
      return;
    }
    if (!window.confirm('Isso vai SUBSTITUIR toda a base de CNPJs atual. Deseja continuar?')) {
      if (cnpjFileInputRef.current) cnpjFileInputRef.current.value = '';
      return;
    }
    setRestoringCnpjs(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Kick off async restore — backend returns {task_id, status:'pending'} immediately.
      const start = await api.post('/admin/restore-cnpjs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // upload only; backend returns quickly
      });
      const taskId = start.data.task_id;
      if (!taskId) {
        // Old endpoint shape — fallback (will run sync).
        alert(start.data.message || 'Conclu?do');
        fetchData();
        return;
      }
      // Poll status every 3s up to 30 minutes (200 attempts).
      let attempts = 0;
      const MAX_ATTEMPTS = 200;
      while (attempts < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 3000));
        attempts += 1;
        let st;
        try {
          st = await api.get(`/admin/restore-cnpjs/status/${taskId}`);
        } catch {
          continue; // transient gateway hiccup — retry
        }
        const s = st.data || {};
        // surface progress to the button label
        setRestoreProgress(s);
        if (s.status === 'completed') {
          alert(s.message || `${s.imported || 0} importados, ${s.skipped || 0} ignorados.`);
          fetchData();
          return;
        }
        if (s.status === 'failed') {
          alert(`Erro ao restaurar: ${s.error || 'desconhecido'}`);
          return;
        }
      }
      alert('Restauracao demorou mais que o esperado. Verifique o status mais tarde.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao restaurar CNPJs: ' + err.message);
    } finally {
      setRestoringCnpjs(false);
      setRestoreProgress(null);
      if (cnpjFileInputRef.current) cnpjFileInputRef.current.value = '';
    }
  };

  const RankingTable = ({ title, icon: Icon, items }) => {
    const total = items?.reduce((sum, i) => sum + i.count, 0) || 0;
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-slate-900">{title}</h3>
        </div>
        {items?.length > 0 ? (
          <div className="space-y-2.5">
            {items.map((item, idx) => {
              const maxCount = items[0]?.count || 1;
              const barPct = (item.count / maxCount) * 100;
              const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
              return (
                <div key={`${item.name}-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: COLORS[idx % COLORS.length] }}>{idx + 1}</span>
                      <span className="text-sm text-slate-700">{item.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-500">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden ml-7">
                    <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, backgroundColor: COLORS[idx % COLORS.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-slate-400">Sem dados no periodo</p>}
      </div>
    );
  };

  const CrossTable = ({ title, items, col1, col2 }) => {
    const total = items?.reduce((sum, i) => sum + i.count, 0) || 0;
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
        {items?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-slate-500 font-medium">{col1}</th>
                  <th className="text-left py-2 text-slate-500 font-medium">{col2}</th>
                  <th className="text-right py-2 text-slate-500 font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
                  return (
                    <tr key={`${item.specialty}-${item.city || item.operator}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-2 text-slate-700">{item.specialty}</td>
                      <td className="py-2 text-slate-600">{item.city || item.operator}</td>
                      <td className="py-2 text-right">
                        <span className="inline-flex items-center justify-center min-w-[42px] h-6 rounded-md text-xs font-bold text-white px-1" style={{ backgroundColor: COLORS[idx % COLORS.length] }}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-400">Sem dados no periodo</p>}
      </div>
    );
  };

  // Prepare radar data from top specialties
  const radarData = data?.top_specialties?.slice(0, 8).map(s => ({
    name: s.name.length > 12 ? s.name.slice(0, 11) + '…' : s.name,
    buscas: s.count,
    fullName: s.name,
  })) || [];

  // Prepare specialties with percentages
  const totalSpecSearches = data?.top_specialties?.reduce((sum, s) => sum + s.count, 0) || 0;
  const specWithPct = data?.top_specialties?.map(s => ({
    ...s,
    pct: totalSpecSearches > 0 ? ((s.count / totalSpecSearches) * 100).toFixed(1) : '0',
  })) || [];

  // Prepare treemap data with percentages
  const totalCitySearches = data?.top_cities?.reduce((sum, c) => sum + c.count, 0) || 0;
  const treemapData = data?.top_cities?.map((c, i) => ({
    name: c.name,
    count: c.count,
    pct: totalCitySearches > 0 ? ((c.count / totalCitySearches) * 100).toFixed(1) : '0',
    fill: COLORS[i % COLORS.length],
  })) || [];
  treemapDataGlobal.current = treemapData;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-search-analytics">
      <Navbar />
      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </Link>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="analytics-title">Inteligencia de Mercado</h1>
            <p className="text-slate-500 mt-1">Análise de buscas e demanda da plataforma</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[7, 30, 90].map(d => (
              <Button key={d} variant={days === d ? "default" : "outline"} size="sm" onClick={() => setDays(d)} data-testid={`period-${d}`}>
                {d}d
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-analytics">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <div className="w-px h-6 bg-slate-200 mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerDownload(`/admin/export-search-logs?days=${days}`, `buscas_${days}d.xlsx`)}
              data-testid="export-search-logs"
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            >
              <Download className="w-4 h-4 mr-1.5" /> Exportar Buscas
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerDownload(`/admin/export-b2b-demand-report?days=${days}`, `relatorio_b2b_${days}d.xlsx`)}
              data-testid="export-b2b-report"
              className="text-violet-700 border-violet-200 hover:bg-violet-50"
            >
              <BarChart3 className="w-4 h-4 mr-1.5" /> Relatório B2B
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerDownload('/admin/export-cnpjs', 'cnpjs_backup.xlsx')}
              data-testid="export-cnpjs"
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              <Database className="w-4 h-4 mr-1.5" /> Backup Base
            </Button>
            <input
              type="file"
              ref={cnpjFileInputRef}
              accept=".xlsx"
              onChange={handleRestoreCnpjs}
              className="hidden"
              data-testid="restore-cnpjs-file-input"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={restoringCnpjs}
              onClick={() => cnpjFileInputRef.current?.click()}
              data-testid="restore-cnpjs"
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {restoringCnpjs ? (restoreProgress?.imported ? `Importando ${restoreProgress.imported.toLocaleString('pt-BR')}...` : 'Restaurando...') : 'Restaurar Base'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx"
              onChange={handleRestore}
              className="hidden"
              data-testid="restore-file-input"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={restoring}
              onClick={() => fileInputRef.current?.click()}
              data-testid="restore-search-logs"
              className="text-amber-700 border-amber-200 hover:bg-amber-50"
            >
              <Upload className="w-4 h-4 mr-1.5" /> {restoring ? 'Restaurando...' : 'Restaurar Buscas'}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-2" data-testid="analytics-tabs">
            <TabsTrigger value="searches" data-testid="tab-searches">Buscas</TabsTrigger>
            <TabsTrigger value="cnpjs" data-testid="tab-cnpjs">CNPJs da Base</TabsTrigger>
          </TabsList>

          <TabsContent value="searches" className="mt-6">

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-64 bg-white rounded-2xl animate-pulse" />)}
          </div>
        ) : !data ? (
          <p className="text-slate-500">Erro ao carregar dados</p>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-primary to-teal-600 rounded-2xl p-5 text-white">
                <p className="text-sm text-teal-100">Total de Buscas</p>
                <p className="text-3xl font-bold mt-1" data-testid="total-searches">{data.total_searches}</p>
                <p className="text-xs text-teal-200 mt-1">Ultimos {days} dias</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
                <p className="text-sm text-orange-100">Especialidades</p>
                <p className="text-3xl font-bold mt-1">{data.unique_specialties || 0}</p>
                <p className="text-xs text-orange-200 mt-1">Distintas buscadas</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-5 text-white">
                <p className="text-sm text-blue-100">Cidades</p>
                <p className="text-3xl font-bold mt-1">{data.unique_cities || 0}</p>
                <p className="text-xs text-blue-200 mt-1">Distintas buscadas</p>
              </div>
              <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
                <p className="text-sm text-violet-100">Operadoras</p>
                <p className="text-3xl font-bold mt-1">{data.unique_operators || 0}</p>
                <p className="text-xs text-violet-200 mt-1">Distintas buscadas</p>
              </div>
            </div>

            {/* Volume Chart - Area with gradient */}
            {data.searches_per_day?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-8">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" /> Volume de Buscas por Dia
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.searches_per_day}>
                    <defs>
                      <linearGradient id="colorBuscas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip content={<CustomTooltipArea />} />
                    <Area type="monotone" dataKey="count" stroke="#0d9488" strokeWidth={2.5} fill="url(#colorBuscas)" dot={{ r: 4, fill: '#0d9488', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} name="Buscas" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Specialties + Cities Bar Charts side by side */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {specWithPct.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" /> Top Buscas Especialidades
                  </h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={specWithPct} layout="vertical" margin={{ left: 0, right: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${v}%`} domain={[0, 'dataMax']} />
                      <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11, fill: '#334155' }} />
                      <Tooltip content={<CustomTooltipBar />} />
                      <Bar dataKey="pct" radius={[0, 6, 6, 0]} name="%" label={{ position: 'right', fontSize: 11, fill: '#475569', formatter: v => `${v}%` }}>
                        {specWithPct.map((entry, i) => <Cell key={entry.name || `spec-${i}`} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {data.top_cities?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" /> Top Buscas Cidades
                  </h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={data.top_cities.map(c => ({ ...c, pct: totalCitySearches > 0 ? ((c.count / totalCitySearches) * 100).toFixed(1) : '0' }))} layout="vertical" margin={{ left: 0, right: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${v}%`} />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: '#334155' }} />
                      <Tooltip content={<CustomTooltipBar />} />
                      <Bar dataKey="pct" radius={[0, 6, 6, 0]} name="%" label={{ position: 'right', fontSize: 11, fill: '#475569', formatter: v => `${v}%` }}>
                        {data.top_cities.map((city, i) => <Cell key={city.name || `city-${i}`} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Radar + Treemap Row */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {radarData.length >= 3 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" /> Radar de Demanda por Especialidade
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} />
                      <PolarRadiusAxis tick={{ fontSize: 9, fill: '#94a3b8' }} />
                      <Radar name="Buscas" dataKey="buscas" stroke="#0d9488" fill="#0d9488" fillOpacity={0.25} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {treemapData.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" /> Mapa de Calor - Cidades
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <Treemap data={treemapData} dataKey="count" nameKey="name" content={<TreemapContent />} animationDuration={500} />
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Rankings Row */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <RankingTable title="Top Buscas Operadoras" icon={Shield} items={data.top_operators} />
              <RankingTable title="Top Buscas Estados" icon={MapPin} items={data.top_states} />
              <RankingTable title="Top Buscas Planos" icon={Shield} items={data.top_plans} />
            </div>

            {/* State Heatmap - Brazil Grid Map */}
            {data.state_heatmap?.length > 0 && <BrazilStateHeatmap data={data.state_heatmap} />}

            {/* Cross Tables */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <CrossTable title="Especialidade x Cidade (Top Combinações)" items={data.cross_specialty_city} col1="Especialidade" col2="Cidade" />
              <CrossTable title="Especialidade x Operadora (Top Combinações)" items={data.cross_specialty_operator} col1="Especialidade" col2="Operadora" />
            </div>

            {/* Lost Opportunities Table */}
            {data.lost_opportunities?.length > 0 && <LostOpportunitiesTable data={data.lost_opportunities} />}

            {/* Demand-Supply Ratio Table */}
            {data.demand_supply_ratio?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-8" data-testid="demand-supply-table">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-slate-900">Demanda x Oferta por Especialidade</h3>
                </div>
                <p className="text-xs text-slate-400 mb-4 ml-7">Indice = Buscas / Profissionais cadastrados. Quanto maior, mais demanda sem oferta.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-500">#</th>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-500">Especialidade</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-500">Buscas</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-500">Profissionais</th>
                        <th className="px-3 py-2.5 text-right font-medium text-slate-500">Indice</th>
                        <th className="px-3 py-2.5 text-left font-medium text-slate-500 w-48">Demanda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.demand_supply_ratio.map((item, idx) => {
                        const maxRatio = data.demand_supply_ratio[0]?.ratio || 1;
                        const barPct = Math.min((item.ratio / maxRatio) * 100, 100);
                        const isHigh = item.ratio >= 3;
                        const isCritical = item.doctor_count === 0;
                        return (
                          <tr key={`${item.specialty}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="px-3 py-2.5">
                              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: COLORS[idx % COLORS.length] }}>{idx + 1}</span>
                            </td>
                            <td className="px-3 py-2.5 text-slate-700 font-medium">
                              <div className="flex items-center gap-1.5">
                                {item.specialty}
                                {isCritical && <AlertTriangle className="w-3.5 h-3.5 text-red-500" title="Sem profissionais cadastrados" />}
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right text-slate-600">{item.search_count}</td>
                            <td className="px-3 py-2.5 text-right">
                              <span className={`font-semibold ${isCritical ? 'text-red-600' : 'text-slate-700'}`}>
                                {item.doctor_count}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <span className={`inline-flex items-center justify-center min-w-[48px] h-6 rounded-md text-xs font-bold text-white px-1.5 ${
                                isCritical ? 'bg-red-500' : isHigh ? 'bg-amber-500' : 'bg-teal-500'
                              }`}>
                                {isCritical ? '∞' : item.ratio.toFixed(1)}
                              </span>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    isCritical ? 'bg-red-500' : isHigh ? 'bg-amber-500' : 'bg-teal-500'
                                  }`}
                                  style={{ width: `${barPct}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Searches */}
            {data.recent_searches?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" /> Buscas Recentes
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Data/Hora</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Origem</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Especialidade</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Sintoma/Doença</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Cidade</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Estado</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Operadora</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-500">Plano</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-500">Resultados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_searches.map((s, idx) => (
                        <tr key={`${s.timestamp || ''}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                            {new Date(s.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">{s.source === 'home' ? 'Home' : 'Busca'}</Badge>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{s.specialty || '-'}</td>
                          <td className="px-3 py-2 text-slate-600">{s.symptom || '-'}</td>
                          <td className="px-3 py-2 text-slate-600">{s.city || '-'}</td>
                          <td className="px-3 py-2 text-slate-600">{s.state || '-'}</td>
                          <td className="px-3 py-2 text-slate-600">{s.operator || '-'}</td>
                          <td className="px-3 py-2 text-slate-600">{s.plan || '-'}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{s.results_count || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
          </TabsContent>

          {/* ============ Tab: CNPJs da Base ============ */}
          <TabsContent value="cnpjs" className="mt-6" data-testid="tab-cnpjs-content">
            {loadingCnpjStats ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-64 bg-white rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : !cnpjStats ? (
              <p className="text-slate-500">Erro ao carregar dados de CNPJs</p>
            ) : (
              <>
                {/* KPI CNPJs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-primary to-teal-600 rounded-2xl p-5 text-white">
                    <p className="text-sm text-teal-100">Total CNPJs Ativos</p>
                    <p className="text-3xl font-bold mt-1" data-testid="cnpj-total">
                      {(cnpjStats.total_doctors || 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-teal-200 mt-1">Na base atual</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
                    <p className="text-sm text-orange-100">Especialidades</p>
                    <p className="text-3xl font-bold mt-1" data-testid="cnpj-total-specialties">
                      {(cnpjStats.total_specialties || 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-orange-200 mt-1">Distintas na base</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-5 text-white">
                    <p className="text-sm text-emerald-100">Cidades</p>
                    <p className="text-3xl font-bold mt-1" data-testid="cnpj-total-cities">
                      {(cnpjStats.total_cities || 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-emerald-200 mt-1">Distintas na base</p>
                  </div>
                </div>

                {/* Top Especialidades + Top Cidades */}
                <div className="grid md:grid-cols-2 gap-6" data-testid="cnpj-rankings">
                  {cnpjStats.top_specialties?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-slate-900">Top Especialidades</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-4">Distribuição de CNPJs por especialidade.</p>
                      <div className="space-y-2.5">
                        {cnpjStats.top_specialties.map((item, idx) => {
                          const max = cnpjStats.top_specialties[0]?.count || 1;
                          const pct = Math.round((item.count / max) * 100);
                          return (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 font-medium truncate pr-2">{idx + 1}. {item.name}</span>
                                <Badge variant="outline" className="shrink-0">{item.count.toLocaleString('pt-BR')}</Badge>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {cnpjStats.top_cities?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-slate-900">Top Cidades</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-4">Distribuição de CNPJs por cidade.</p>
                      <div className="space-y-2.5">
                        {cnpjStats.top_cities.map((item, idx) => {
                          const max = cnpjStats.top_cities[0]?.count || 1;
                          const pct = Math.round((item.count / max) * 100);
                          return (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 font-medium truncate pr-2">{idx + 1}. {item.name}</span>
                                <Badge variant="outline" className="shrink-0">{item.count.toLocaleString('pt-BR')}</Badge>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom 10: gaps de cobertura — especialidades e cidades com menor presença de CNPJs */}
                <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="cnpj-bottoms">
                  {cnpjStats.bottom_specialties?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-900">Especialidades com menos CNPJs</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-4">Gaps de cobertura — oportunidades para o scraper expandir.</p>
                      <div className="space-y-2.5">
                        {cnpjStats.bottom_specialties.map((item, idx) => {
                          const max = cnpjStats.bottom_specialties[cnpjStats.bottom_specialties.length - 1]?.count || 1;
                          const pct = Math.max(8, Math.round((item.count / max) * 100));
                          return (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 font-medium truncate pr-2">{idx + 1}. {item.name}</span>
                                <Badge variant="outline" className="shrink-0 text-amber-700 border-amber-200">{item.count.toLocaleString('pt-BR')}</Badge>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {cnpjStats.bottom_cities?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-100 p-6">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-900">Cidades com menos CNPJs</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-4">Gaps de cobertura — oportunidades para o scraper expandir.</p>
                      <div className="space-y-2.5">
                        {cnpjStats.bottom_cities.map((item, idx) => {
                          const max = cnpjStats.bottom_cities[cnpjStats.bottom_cities.length - 1]?.count || 1;
                          const pct = Math.max(8, Math.round((item.count / max) * 100));
                          return (
                            <div key={idx} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-700 font-medium truncate pr-2">{idx + 1}. {item.name}</span>
                                <Badge variant="outline" className="shrink-0 text-amber-700 border-amber-200">{item.count.toLocaleString('pt-BR')}</Badge>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
